import { Popover, Transition } from '@headlessui/react';
import classNames from 'classnames';
import React, { PropsWithChildren } from 'react';

export type ActionButtonProps = PropsWithChildren<{
  className?: string;
  disabled?: boolean;
  disabledReason?: JSX.Element;
  onClick?: () => any;
}>;

/**
 * The viewer's one main action (Validate), drawn the design's way: filled plum when it can be pressed, a clearly
 * disabled grey when it cannot — never a grey that only looks disabled. When a reason is given, the disabled button
 * still opens it, in a popover. The colours are the viewer surface's `dl-*` tokens.
 */
export const actionButtonClasses = {
  base: 'inline-flex h-9 min-w-[96px] items-center justify-center gap-2 rounded-[9px] px-4 font-sans text-sm font-semibold leading-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-dl-accent focus-visible:ring-offset-2 focus-visible:ring-offset-dl-chrome',
  enabled: 'bg-dl-primary text-white hover:bg-dl-primary-hover',
  disabled: 'cursor-not-allowed bg-dl-border text-dl-faint',
};

export function ActionButton({
  children,
  disabled,
  disabledReason,
  onClick,
  className,
}: ActionButtonProps) {
  if (disabled && disabledReason) {
    return (
      <Popover className={classNames('relative', className)}>
        {/* no aria-disabled: Headless UI drops every handler of a button that has it, and this one must still open */}
        <Popover.Button className="h-full w-full overflow-hidden rounded-[9px] focus:outline-none focus-visible:ring-2 focus-visible:ring-dl-accent">
          {children}
        </Popover.Button>
        <Transition
          enter="transition duration-100 ease-out"
          enterFrom="transform scale-95 opacity-0"
          enterTo="transform scale-100 opacity-100"
          leave="transition duration-75 ease-out"
          leaveFrom="transform scale-100 opacity-100"
          leaveTo="transform scale-95 opacity-0"
        >
          <Popover.Panel className="absolute right-0 z-30 mt-2 w-[30rem] rounded-xl border border-dl-border bg-dl-raised p-4 text-base text-dl-text shadow-lg">
            {disabledReason}
          </Popover.Panel>
        </Transition>
      </Popover>
    );
  }

  return (
    <button
      type="button"
      className={classNames(
        actionButtonClasses.base,
        disabled ? actionButtonClasses.disabled : actionButtonClasses.enabled,
        className
      )}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
    >
      {children}
    </button>
  );
}
